// src/components/features/chat/AppointmentPaymentCard.jsx
//
// 16 Jul 2026 — new. Same pattern as LabTestPaymentCard.jsx: renders when
// the chat's create_appointment action resolves — clicking the card is
// what actually opens Razorpay Checkout and, on success, creates the
// appointment. Uses the standard Razorpay Checkout modal (confirmed with
// Deepu for the lab-test card; same choice applies here — not a custom
// panel or embedded screen).
//
// 27 Jul 2026 — added a wallet payment option, mirroring
// LabTestPaymentCard.jsx exactly.

import React, { useState, useEffect } from 'react';
import { payAndCreateAppointment, payAndCreateAppointmentWithWallet } from '../../../utils/appointmentPayment';
import { walletAPI } from '../../../services/api';
import Icon from '../../Icon';

const C = {
  navy:       'var(--brand-dark)',
  gold:       'var(--amber-text)',
  goldLight:  'var(--amber-bg)',
  goldBorder: 'var(--amber)',
  white:      'var(--bg-surface)',
  textPrimary:'var(--text-primary)',
  textMuted:  'var(--text-muted)',
  good:       'var(--green)',
  goodTint:   'var(--green-bg)',
  bad:        'var(--red)',
  badTint:    'var(--red-bg)',
};

function formatRupees(paise) {
  if (paise == null) return '—';
  return `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
}

export default function AppointmentPaymentCard({
  patient, doctorId, doctorName, appointmentType, amountPaise, appointmentPayload,
}) {
  const [status, setStatus]   = useState('idle'); // idle | processing | success | failed
  const [message, setMessage] = useState('');
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (patient?.id) {
      walletAPI.getBalance(patient.id)
        .then((res) => { if (!cancelled) setWalletBalanceMinorUnits(res.data.balanceMinorUnits || 0); })
        .catch(() => { /* silent — wallet option just won't show if this fails */ });
    }
    return () => { cancelled = true; };
  }, [patient?.id]);

  const runPayment = async (usingWallet) => {
    if (status === 'processing' || status === 'success') return;
    setStatus('processing');
    setMessage('');

    const payFn = usingWallet ? payAndCreateAppointmentWithWallet : payAndCreateAppointment;
    const result = await payFn({
      patientId:    patient?.id,
      doctorId,
      appointmentType,
      doctorName,
      patientName:  `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
      patientEmail: patient?.email,
      patientPhone: patient?.phone,
      appointmentPayload,
    });

    if (result.success) {
      setStatus('success');
      return;
    }

    setStatus('failed');
    if (result.reason === 'payment_cancelled') {
      setMessage('Payment was not completed. Click to try again.');
    } else if (result.reason === 'payment_failed') {
      setMessage(`Payment failed: ${result.message || 'please try again.'}`);
    } else if (result.reason === 'appointment_creation_failed') {
      setMessage(result.message);
    } else {
      setMessage(result.message || 'Something went wrong. Click to try again.');
    }
  };

  const clickable = status === 'idle' || status === 'failed';
  const showWalletOption = clickable && walletBalanceMinorUnits > 0;

  return (
    <div style={{ maxWidth: 320 }}>
      <div
        style={{
          border: `1.5px solid ${status === 'success' ? C.good : status === 'failed' ? C.bad : C.goldBorder}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: C.white,
          boxShadow: '0 1px 3px rgba(13,30,53,0.08)',
        }}
      >
        <div style={{ background: C.navy, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="stethoscope" size={16} />
          <span style={{ color: C.white, fontSize: 12, fontWeight: 600, letterSpacing: 0.3 }}>
            DOCTOR APPOINTMENT
          </span>
        </div>

        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>
            {doctorName || 'Doctor Consultation'}
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
            {formatRupees(amountPaise)}
          </div>

          {status === 'success' ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 8, background: C.goodTint,
              fontSize: 12, fontWeight: 600, color: C.good,
            }}>
              ✅ Appointment confirmed
            </div>
          ) : (
            <>
              <div
                role="button"
                tabIndex={clickable ? 0 : -1}
                onClick={clickable ? () => runPayment(false) : undefined}
                onKeyDown={(e) => {
                  if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); runPayment(false); }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 12px', borderRadius: 8,
                  background: status === 'failed' ? C.badTint : C.goldLight,
                  border: `1px solid ${status === 'failed' ? C.bad : C.goldBorder}`,
                  fontSize: 12, fontWeight: 600,
                  color: status === 'failed' ? C.bad : C.gold,
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                {status === 'processing' ? '⏳ Processing…' : status === 'failed' ? '⚠ Retry payment' : '💳 Pay & Book Appointment'}
              </div>

              {showWalletOption && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => runPayment(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); runPayment(true); }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 8, marginTop: 8,
                    background: C.white, border: `1px solid ${C.navy}`,
                    fontSize: 12, fontWeight: 600, color: C.navy, cursor: 'pointer',
                  }}
                >
                  👛 Pay with wallet ({formatRupees(walletBalanceMinorUnits)} available)
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {status === 'failed' && message && (
        <div style={{ marginTop: 6, fontSize: 11, color: C.bad }}>{message}</div>
      )}
    </div>
  );
}
